import React, { useCallback } from 'react';
import { Box, Paper, Typography, Checkbox, useTheme, Button } from '@mui/material';
import { TableCell } from '@mui/material';
import useTouchInteraction from '../../../hooks/useTouchInteraction';

const PositionCard = React.memo(({ 
  position, 
  selectedPosition, 
  handleCheckbox, 
  formatProduct, 
  exchangeSpecificStringifiedNumber,
  onRowClick
}) => {
  const theme = useTheme();
  
  const isSelected = selectedPosition.some(
    item => item.token === position.token &&
      item.exchange === position.exchange &&
      item.product === position.product
  );

  const handleTap = useCallback((e) => {
    // Skip if the click is on the checkbox container
    if (e.target.closest('.checkbox-container')) {
      return; // Checkbox clicks are handled by the container's onClick
    } 
    
    // If not clicking on checkbox, open the details drawer
    onRowClick(position);
  }, [position, onRowClick]);

  const { handlers, touchStates } = useTouchInteraction(handleTap, true);

  return (
    <Paper
      elevation={0}
      {...handlers}
      component="div"
      role="button"
      sx={{ 
        mb: 0.5,
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        cursor: position.net_quantity !== 0 ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        userSelect: 'none',
        touchAction: 'pan-y',
        WebkitUserSelect: 'none',
        '&:active': position.net_quantity !== 0 ? {
          backgroundColor: touchStates.isActive && !touchStates.isTouchMoved 
            ? theme.palette.action.selected 
            : theme.palette.background.paper,
        } : {},
        transition: 'background-color 0.15s ease'
      }}
    >
      <Box 
        sx={{
          p: 1,
          pt: 0.75,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        <Box sx={{
          mb: 0.75,
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start'
        }}>
          <Box sx={{ pr: 4 }}> 
            <Typography 
              variant="subtitle2" 
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                color: theme.palette.text.primary,
                lineHeight: 1.1,
                pointerEvents: 'none'
              }}
            >
              {position.trading_symbol}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{
                fontSize: '0.7rem',
                color: theme.palette.text.secondary,
                lineHeight: 1.1,
                pointerEvents: 'none'
              }}
            >
              {position.exchange} • {formatProduct(position.product)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box 
              className="checkbox-container"
              sx={{ 
                zIndex: 1
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (position.net_quantity !== 0) {
                  const syntheticEvent = {
                    target: {
                      checked: !isSelected,
                      name: ''
                    },
                    preventDefault: () => {},
                    stopPropagation: () => {}
                  };
                  handleCheckbox(syntheticEvent, position);
                }
              }}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                disabled={position.net_quantity === 0}
                size="small"
                sx={{ 
                  p: 0,
                  pointerEvents: 'none',
                  '& .MuiSvgIcon-root': {
                    fontSize: '1.25rem'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr',
          gap: 0.5,
          bgcolor: theme.palette.background.default,
          borderRadius: 1,
          p: 0.5
        }}>
          <MetricBox
            label="Qty"
            value={position.net_quantity}
            valueColor={position.net_quantity > 0 
              ? theme.palette.success.main 
              : position.net_quantity < 0 
              ? theme.palette.error.main 
              : theme.palette.text.primary}
          />
          <MetricBox
            label="Buy Avg"
            value={exchangeSpecificStringifiedNumber(position.exchange, position.buyAverage)}
          />
          <MetricBox
            label="Sell Avg"
            value={exchangeSpecificStringifiedNumber(position.exchange, position.sellAverage)}
          />
          <MetricBox
            label="Price"
            value={exchangeSpecificStringifiedNumber(position.exchange, position.ltp)}
            valueColor={position.net_quantity > 0 
              ? theme.palette.success.main 
              : position.net_quantity < 0 
              ? theme.palette.error.main 
              : theme.palette.text.primary}
          />
          <MetricBox
            label="P&L"
            value={`₹${exchangeSpecificStringifiedNumber(position.exchange, Math.abs(position.pnl))}`}
            valueColor={position.pnl >= 0 ? theme.palette.success.main : theme.palette.error.main}
            align="right"
          />
        </Box>
      </Box>
    </Paper>
  );
});

const MetricBox = ({ label, value, valueColor = 'text.primary', align = 'left' }) => {
  const theme = useTheme();
  return (
    <Box sx={{ textAlign: align }}>
      <Typography 
        variant="caption" 
        sx={{
          fontSize: '0.6rem',
          lineHeight: 1,
          display: 'block',
          color: theme.palette.text.secondary
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ 
          fontWeight: 600,
          color: valueColor,
          fontSize: '0.75rem',
          lineHeight: 1.2,
          mt: 0.25
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const HeaderCell = ({ children, width, align = 'left' }) => {
  const theme = useTheme();
  return (
    <TableCell
      align={align}
      sx={{
        bgcolor: theme.palette.background.paper,
        fontWeight: 600,
        borderBottom: `1px solid ${theme.palette.divider}`,
        py: 1.25,
        px: 1.5,
        width,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        whiteSpace: 'nowrap',
        color: theme.palette.text.secondary,
        fontSize: '0.813rem',
      }}
    >
      {children}
    </TableCell>
  );
};

const MobileView = ({ 
  positions, 
  selectedPosition, 
  handleCheckbox, 
  formatProduct, 
  exchangeSpecificStringifiedNumber,
  onRowClick
}) => {
  const theme = useTheme();

  return (
    <Box sx={{
      px: 1,
      py: 0.5,
      WebkitTapHighlightColor: 'transparent',
      backgroundColor: theme.palette.background.default,
      height: '100%',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'none',
      touchAction: 'pan-y',
      '&::-webkit-scrollbar': {
        display: 'none'
      },
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      pb: '120px'
    }}>
      {positions?.map((position) => (
        <PositionCard 
          key={position.token + position.exchange + position.product}
          position={position}
          selectedPosition={selectedPosition}
          handleCheckbox={handleCheckbox}
          formatProduct={formatProduct}
          exchangeSpecificStringifiedNumber={exchangeSpecificStringifiedNumber}
          onRowClick={onRowClick}
        />
      ))}
    </Box>
  );
};

export default MobileView; 